import { useEffect, useState } from 'react'
import { Users, Activity, Database, AlertTriangle } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api'

interface SystemAnalytics {
  total_users: number
  active_users_30d: number
  total_requests_30d: number
  popular_endpoints: Array<{ endpoint: string; count: number }>
  error_rate: number
  uptime_percentage: number
}

interface CreditUsage {
  daily_usage: Array<{ date: string; requests: number }>
}

// Admin Panel Redesign: the old "overview" tab's daily-volume chart only
// rendered if the Credits tab had already been visited in the same session
// (creditUsage state was shared and only fetched when activeTab === 'credits').
// Fixed by fetching its own 30-day daily_usage here, independent of Credits.
export default function OverviewSection() {
  const [analytics, setAnalytics] = useState<SystemAnalytics | null>(null)
  const [dailyUsage, setDailyUsage] = useState<CreditUsage['daily_usage']>([])
  const [userCounts, setUserCounts] = useState({ total: 0, active: 0, admin: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    (async () => {
      try {
        setLoading(true)
        const end = new Date()
        const start = new Date()
        start.setDate(start.getDate() - 30)
        const [analyticsData, usersData, credits] = await Promise.all([
          apiClient.getSystemAnalytics(),
          apiClient.getAllUsers(0, 1000),
          apiClient.getCreditUsage(start.toISOString().split('T')[0], end.toISOString().split('T')[0]).catch(() => null),
        ])
        setAnalytics(analyticsData as SystemAnalytics)
        const users = (usersData as any[]) || []
        setUserCounts({
          total: users.length,
          active: users.filter((u) => u.is_active).length,
          admin: users.filter((u) => u.role === 'admin').length,
        })
        setDailyUsage((credits as CreditUsage | null)?.daily_usage || [])
      } catch (e) {
        console.error('Failed to load overview data', e)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Total Users</CardTitle>
            <Users className="w-5 h-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{userCounts.total}</div>
            <p className="text-sm text-muted-foreground mt-2">
              {userCounts.active} active, {userCounts.admin} admin
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">System Status</CardTitle>
            <Activity className="w-5 h-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{analytics?.uptime_percentage.toFixed(1) ?? '0'}%</div>
            <p className="text-sm text-muted-foreground mt-2">System uptime</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Total Requests</CardTitle>
            <Database className="w-5 h-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{analytics?.total_requests_30d ?? 0}</div>
            <p className="text-sm text-muted-foreground mt-2">Last 30 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Error Rate</CardTitle>
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{analytics ? (analytics.error_rate * 100).toFixed(1) : '0'}%</div>
            <p className="text-sm text-muted-foreground mt-2">System errors</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Active Users (30d)</CardTitle>
            <Activity className="w-5 h-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{analytics?.active_users_30d ?? '—'}</div>
            <p className="text-sm text-muted-foreground mt-2">Unique active users</p>
          </CardContent>
        </Card>
      </div>

      {!loading && dailyUsage.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Daily Request Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dailyUsage}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="requests" fill="#22c55e" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {analytics && (
        <Card>
          <CardHeader>
            <CardTitle>Popular Endpoints</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.popular_endpoints.slice(0, 10).map((ep, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 border rounded">
                  <span className="font-mono text-sm">{ep.endpoint}</span>
                  <Badge>{ep.count} requests</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
